import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatTimeFromHhMm, formatTimeOptionLabel, usesHour12 } from "../../utils/regionalFormat";
import { useRegionalSettings } from "../../context/RegionalSettingsContext";
import { filterTimeOptionObjects } from "../../utils/timeOptions";

function pad(n) {
    return String(n).padStart(2, "0");
}

function generateAllTimeOptions() {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
            const value = `${pad(hour)}:${pad(minute)}`;
            options.push({
                value,
                label: formatTimeOptionLabel(value),
            });
        }
    }
    return options;
}

export function TimePicker15Min({
    value,
    onChange,
    placeholder,
    className = "",
    required = false,
    label = "",
    showDuration = false,
    otherTime = null,
    /** HH:mm — end picker: only times strictly after this */
    minTime = null,
    /** HH:mm — start picker: only times strictly before this */
    maxTime = null,
    disabled = false,
}) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [displayValue, setDisplayValue] = useState("");
    const wrapperRef = useRef(null);
    const dropdownRef = useRef(null);
    const { settings } = useRegionalSettings();

    const timeOptions = useMemo(
        () => filterTimeOptionObjects(generateAllTimeOptions(), { minTime, maxTime, keepValue: value }),
        [minTime, maxTime, value, settings?.timeFormat]
    );

    const calculateDuration = () => {
        if (!showDuration || !value || !otherTime) return null;
        const [h1, m1] = value.split(':').map(Number);
        const [h2, m2] = otherTime.split(':').map(Number);
        const mins1 = h1 * 60 + m1;
        const mins2 = h2 * 60 + m2;
        const diffMins = Math.abs(mins2 - mins1);
        const hours = Math.floor(diffMins / 60);
        const minutes = diffMins % 60;
        return { hours, minutes, total: diffMins / 60, exceeds3Hours: diffMins / 60 > 3 };
    };

    const duration = calculateDuration();

    useEffect(() => {
        setDisplayValue(value ? formatTimeFromHhMm(value) : "");
    }, [value, settings?.timeFormat]);

    useEffect(() => {
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
        if (open && value && dropdownRef.current) {
            const selectedButton = dropdownRef.current.querySelector(`[data-value="${value}"]`);
            if (selectedButton) {
                selectedButton.scrollIntoView({ block: 'center' });
            }
        }
    }, [open, value]);

    const handleSelect = (timeValue) => {
        onChange(timeValue);
        setOpen(false);
    };

    const handleClear = () => {
        onChange("");
        setOpen(false);
    };

    const clockHint = usesHour12() ? t('datePicker.hour12') : t('datePicker.hour24');

    return (
        <div ref={wrapperRef} className="relative">
            {label ? <label className="form-label">{label}</label> : null}
            <input
                type="text"
                value={displayValue}
                onClick={() => !disabled && setOpen(!open)}
                placeholder={placeholder ?? t('datePicker.selectTime')}
                readOnly
                required={required}
                disabled={disabled}
                className={`input ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} ${className}`.trim()}
            />

            {duration && (
                <div className={`text-xs mt-1 font-medium ${duration.exceeds3Hours ? 'text-red-600' : 'text-gray-600'}`}>
                    {t('datePicker.duration', { hours: duration.hours, minutes: duration.minutes })}
                    {duration.exceeds3Hours && ` ⚠️ ${t('datePicker.exceeds3Hours')}`}
                </div>
            )}

            {open && (
                <div className="absolute z-50 mt-1 w-64 bg-white border border-gray-300 rounded-lg shadow-xl">
                    <div className="p-2 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                        <div className="text-xs font-medium text-gray-600 uppercase">
                            {t('datePicker.selectTimeWithFormat', { format: clockHint })}
                        </div>
                    </div>
                    <div ref={dropdownRef} className="max-h-64 overflow-y-auto">
                        {timeOptions.map((opt) => (
                            <button
                                key={opt.value}
                                data-value={opt.value}
                                type="button"
                                onClick={() => handleSelect(opt.value)}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-primary-50 transition-colors ${value === opt.value
                                        ? 'bg-primary-100 text-primary-700 font-semibold'
                                        : 'text-gray-700'
                                    }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <div className="p-2 border-t border-gray-200 bg-gray-50 rounded-b-lg flex gap-2">
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
                        >
                            {t('common.done')}
                        </button>
                        <button
                            type="button"
                            onClick={handleClear}
                            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
                        >
                            {t('datePicker.clear')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
